import { FC, useEffect, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { useNotification } from '@/components/notifications';
import { useGetCollectionInfo } from '@/hooks/queries/data-sources/use-get-collection-info';
import { useGetSamplePoint } from '@/hooks/queries/data-sources/use-get-sample-point';
import { usePatchCollectionPointsPayload } from '@/hooks/mutations/data-sources/use-patch-collection-points-payload';
import { useOrgStore } from '@/stores/org-store';
import { Source } from '@/types/source-types';
import {
  FieldOverrides,
  FieldOverridesForm,
  buildFieldOverridesPayload,
} from './field-overrides-form';
import { resolveArrayFields } from './field-overrides-utils';

interface EditFileFieldsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  collectionId: string;
  source: Source | null;
  exposedAttributes: string[];
  exposedCardinality?: Record<string, 'single' | 'multi'> | null;
  onSaved?: () => void;
}

export const EditFileFieldsModal: FC<EditFileFieldsModalProps> = ({
  isOpen,
  onOpenChange,
  collectionId,
  source,
  exposedAttributes,
  exposedCardinality,
  onSaved,
}) => {
  const notification = useNotification();
  const orgId = useOrgStore((state) => state.currentOrg?.orgid);
  const [values, setValues] = useState<FieldOverrides>({});

  const externalId = source?.tags?.external_id;
  const hasExposedAttrs = exposedAttributes.length > 0;

  const { data: collectionInfo, isLoading: isSchemaLoading } =
    useGetCollectionInfo({
      collectionId: isOpen && hasExposedAttrs ? collectionId : undefined,
      orgId,
      enabled: isOpen && hasExposedAttrs,
    });

  const shouldQuerySample = Boolean(
    isOpen && hasExposedAttrs && externalId && orgId,
  );

  const {
    data: sampleData,
    isLoading: isSampleLoading,
    isError: isSampleError,
    error: sampleError,
  } = useGetSamplePoint({
    collectionId: shouldQuerySample ? collectionId : undefined,
    externalId: shouldQuerySample ? externalId : undefined,
    orgId,
    enabled: shouldQuerySample,
  });

  const samplePayload = useMemo(
    () => sampleData?.points?.[0]?.payload ?? null,
    [sampleData],
  );

  const arrayFields = useMemo(
    () =>
      resolveArrayFields(exposedAttributes, samplePayload, exposedCardinality),
    [samplePayload, exposedAttributes, exposedCardinality],
  );

  const patchMutation = usePatchCollectionPointsPayload({
    onSuccess: (res) => {
      const count = res.points_updated ?? 0;
      if (count === 0) {
        notification.warning(
          'No points were updated. The file may still be ingesting or has no indexed points yet.',
        );
      } else {
        notification.success(`Updated ${count} point${count === 1 ? '' : 's'}`);
      }
      onSaved?.();
      onOpenChange(false);
    },
    onError: (error) => {
      notification.error(error.message || 'Failed to update file fields');
      Sentry.captureException(error, {
        extra: { context: 'patch-collection-points-payload', collectionId },
      });
    },
  });

  useEffect(() => {
    if (!isOpen) {
      setValues({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (!samplePayload) return;
    const seeded: FieldOverrides = {};
    exposedAttributes.forEach((field) => {
      const existing = samplePayload[field];
      if (existing === undefined || existing === null) return;
      if (Array.isArray(existing)) {
        seeded[field] = existing.map((v) =>
          typeof v === 'string' ? v : String(v),
        );
      } else if (
        typeof existing === 'string' ||
        typeof existing === 'number' ||
        typeof existing === 'boolean'
      ) {
        seeded[field] = existing;
      } else {
        seeded[field] = String(existing);
      }
    });
    setValues(seeded);
  }, [samplePayload, exposedAttributes]);

  const title = useMemo(() => {
    const name = source?.original_filename || source?.filename || 'file';
    return `Edit fields — ${name}`;
  }, [source]);

  const handleSave = () => {
    if (!externalId) {
      notification.error('File is missing an external_id tag');
      return;
    }
    const payload = buildFieldOverridesPayload(
      exposedAttributes,
      values,
      collectionInfo?.payload_schema,
    );
    if (Object.keys(payload).length === 0) {
      notification.error('Enter at least one field value');
      return;
    }
    patchMutation.mutate({ collectionId, externalId, payload });
  };

  const footer = (
    <div className='flex gap-2'>
      <Button
        variant='outline'
        onClick={() => onOpenChange(false)}
        disabled={patchMutation.isPending}
      >
        Cancel
      </Button>
      <Button
        onClick={handleSave}
        disabled={patchMutation.isPending || !externalId}
      >
        {patchMutation.isPending ? 'Saving...' : 'Save'}
      </Button>
    </div>
  );

  return (
    <Modal
      isDialogOpen={isOpen}
      setIsDialogOpen={onOpenChange}
      title={title}
      description='Updates apply to all Qdrant points that share this file’s external_id.'
      footer={footer}
    >
      <div className='space-y-4'>
        {source && !externalId && (
          <p className='text-sm text-red-500'>
            This file has no external_id tag. It may still be ingesting or
            predates the feature.
          </p>
        )}
        {externalId && (isSchemaLoading || isSampleLoading) && (
          <p className='text-sm text-muted-foreground'>
            Loading existing field values...
          </p>
        )}
        {externalId &&
          !isSchemaLoading &&
          !isSampleLoading &&
          isSampleError && (
            <p className='text-sm text-amber-600'>
              Could not load existing values
              {sampleError instanceof Error
                ? `: ${sampleError.message}`
                : '.'}{' '}
              You may still enter new values below.
            </p>
          )}
        {externalId && !isSchemaLoading && !isSampleLoading && (
          <FieldOverridesForm
            exposedAttributes={exposedAttributes}
            payloadSchema={collectionInfo?.payload_schema}
            values={values}
            onChange={setValues}
            disabled={patchMutation.isPending || !externalId}
            arrayFields={arrayFields}
          />
        )}
      </div>
    </Modal>
  );
};
